/**
 * RequisitionOrderingSettingsSection Component
 *
 * Whether an approved requisition raises a draft purchase order on its own. Stored as it flips.
 */
import { useMutation, useQuery } from '@apollo/client/react';
import { useEffect, useState } from 'react';
import { Checkbox, Loader } from '@insio/ui-toolkit';
import {
  RequisitionAutomationSettingsDocument,
  UpdateRequisitionAutomationSettingsDocument
} from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { useCanChangeAutomationSettings } from './use-can-change-automation-settings';

export function RequisitionOrderingSettingsSection() {
  const t = useFeatureTranslation('systemSettings.automation.requisitionOrdering');
  const { showError } = useToastHelpers();
  const canChange = useCanChangeAutomationSettings();

  const { data, loading, error } = useQuery(RequisitionAutomationSettingsDocument);
  // The response carries no id to normalise, so the settings query is refetched —
  // otherwise the checkbox keeps comparing against the pre-save snapshot.
  const [updateSettings] = useMutation(UpdateRequisitionAutomationSettingsDocument, {
    refetchQueries: [{ query: RequisitionAutomationSettingsDocument }],
    awaitRefetchQueries: true
  });

  const [isAutoOrderEnabled, setIsAutoOrderEnabled] = useState(true);

  const settings = data?.requisitionAutomationSettings;

  useEffect(() => {
    if (settings) {
      setIsAutoOrderEnabled(settings.isAutoOrderEnabled);
    }
  }, [settings]);

  /** Stored the moment it flips; a refused write flips it back. */
  const handleChange = async (next: boolean) => {
    if (!canChange) return;
    setIsAutoOrderEnabled(next);
    try {
      // errorPolicy 'none' (vs global 'all') makes GraphQL errors throw into the catch below
      await updateSettings({
        variables: { input: { isAutoOrderEnabled: next } },
        errorPolicy: 'none'
      });
    } catch {
      setIsAutoOrderEnabled(settings?.isAutoOrderEnabled ?? !next);
      showError(t('saveError'));
    }
  };

  if (loading && !settings) {
    return <Loader.Section />;
  }

  if (error) {
    return <p className='text-sm text-error-text'>{t('loadError')}</p>;
  }

  return (
    <div className='space-y-lg'>
      <div>
        <h2 className='text-xl font-bold text-foreground-base'>{t('title')}</h2>
        <p className='text-sm text-foreground-muted mt-xs'>{t('description')}</p>
      </div>

      <Checkbox
        checked={isAutoOrderEnabled}
        onChange={handleChange}
        disabled={!canChange}
        label={t('autoOrder.label')}
        description={t('autoOrder.description')}
        testId='requisition-ordering-auto-order'
      />
    </div>
  );
}
